import Cookies from "js-cookie";

class GdprOverlay {
  root = null;
  checkboxes = [];

  constructor() {
    this.root = document.querySelector("#gdpr-overlay");

    this.checkboxes = Array.from(
      this.root.querySelectorAll("[data-gdpr-script-id]")
    );
  }

  getSelectedScripts() {
    return this.checkboxes
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.getAttribute("data-gdpr-script-id"));
  }

  declineAll() {
    this.checkboxes.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = false;
      }
    });

    this.hide();
  }

  acceptSelected() {
    this.hide();
  }

  acceptAll() {
    this.checkboxes.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = true;
      }
    });
    this.hide();
  }

  display() {
    this.root.removeAttribute("style");
  }

  hide() {
    this.root.style.setProperty("display", "none");
    window.dispatchEvent(
      new CustomEvent("gdpr-overlay-close", {
        detail: {
          acceptedScripts: this.getSelectedScripts(),
        },
      })
    );
  }
}

class Gdpr {
  overlay = null;
  cookieKey = null;
  scripts = null;
  loadedScripts = [];
  acceptedScripts = null;

  constructor() {
    this.overlay = new GdprOverlay();

    this.setCookieKey();

    if (!this.cookieKey) {
      return;
    }

    this.setScripts();
    this.setAcceptedScripts();
    this.loadHiddenActiveScripts();

    if (this.acceptedScripts !== false) {
      this.loadAcceptedScripts();
    } else if(!document.body.hasAttribute('data-gdpr-overlay-disabled')) {
      this.displayOverlayOnMinimalUserInteraction();
    }

    window.addEventListener("gdpr-overlay-close", (e) => {
      this.acceptScripts(e.detail.acceptedScripts);

      e.detail.acceptedScripts.forEach((script) => {
        this.loadScript(script);
      });
    });
  }

  setScripts() {
    const json = document.querySelector("#gdpr-scripts")?.innerText;
    this.scripts = json ? JSON.parse(json) : [];
  }

  setCookieKey() {
    const version = document.body.getAttribute("data-gdpr-version");

    if (version) {
      this.cookieKey = "gdpr-accepted-scripts-" + version;
    }
  }

  setAcceptedScripts() {
    const json = Cookies.get(this.cookieKey);

    if (json) {
      this.acceptedScripts = JSON.parse(
        Cookies.get(this.cookieKey)
      )?.acceptedScripts;
    }else{
      this.acceptedScripts = false;
    }
  }

  acceptScripts(scripts) {
    Cookies.set(
      this.cookieKey,
      JSON.stringify({
        acceptedScripts: scripts,
      })
    );
  }

  displayOverlayOnMinimalUserInteraction() {
    window.addEventListener(
      "minimal-user-interaction",
      () => {
        this.overlay.display();
      },
      { once: true }
    );
  }

  loadHiddenActiveScripts() {
    Object.values(this.scripts).forEach((script) => {
      if (script.defaultValue && script.hideInOverlay) {
        this.loadScript(script.id);
      }
    });
  }

  loadAcceptedScripts() {
    this.acceptedScripts.forEach((script) => {
      this.loadScript(script);
    });
  }

  loadScript(id) {
    if (this.loadedScripts.includes(id)) {
      return;
    }

    this.loadedScripts.push(id);
    console.log("GDPR: Loaded script with id " + id);

    // Dispatch event immediately
    window.dispatchEvent(
      new CustomEvent("gdpr-script-load", {
        detail: {
          scriptId: id,
        },
      })
    );

    // Dispatch event after alpinejs is initialized to catch the event in an alpinejs component
    document.addEventListener('alpine:initialized', () => {
      window.dispatchEvent(
        new CustomEvent("gdpr-script-load", {
          detail: {
            scriptId: id,
          },
        })
      );
    })

    if (this.scripts[id].code) {
      this.loadScriptCode(this.scripts[id]);
    }
  }

  loadScriptCode(script) {
    const code = script.code;
    const position = script.codePosition;

    const elements = this.getElementsToInsert(code);

    if (position === "head") {
      elements.forEach((element) => {
        document.head.appendChild(element);
      });
    } else if (position === "body-closing") {
      elements.forEach((element) => {
        document.body.appendChild(element);
      });
    } else if (position === "body-opening") {
      elements.forEach((element) => {
        document.body.insertBefore(element, document.body.firstChild);
      });
    }
  }

  getElementsToInsert(html) {
    const container = document.createElement("div");
    container.innerHTML = html;

    const elements = [];

    container.childNodes.forEach((child) => {
      // Not-Script HTML-Elements
      if (child.tagName !== "SCRIPT") {
        elements.push(child);
        return;
      }

      // Create custom script element to be executed
      const script = document.createElement("script");

      // Copy attributes (e.g., src, type, async, etc.)
      for (const attr of child.attributes) {
        script.setAttribute(attr.name, attr.value);
      }

      // Copy inline script content
      if (child.textContent) {
        script.textContent = child.textContent;
      }

      elements.push(script);
    });

    return elements;
  }
}

window.gdpr = new Gdpr();
